"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import SDFLogo from "../../../public/SDF-Logo.png";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";

const NAV_ITEMS = [
  {
    label: "About",
    href: "/wp/about/",
    external: false,
    items: [
      { text: "Our Mission", href: "/wp/about/mission/", external: false },
      { text: "Our Team", href: "/wp/about/our-team/", external: false },
      {
        text: "Our Method",
        href: "/wp/about/document-sourcing/",
        external: false,
      },
      { text: "Our Thanks", href: "/wp/about/our-thanks/", external: false },
      { text: "Framework Hub", href: "/framework-hub/", external: false },
    ],
  },

  {
    label: "Search",
    href: "/search/",
    items: [
      {
        text: "Search Tips",
        href: "/wp/search-tips/",
        external: true,
      },
    ],
  },
  {
    label: "Analytics",
    href: "/analytics/",
  },
  {
    label: "News",
    href: "/wp/additional-resources/featured-news",
    external: false
  },
  {
    label: "Resources",
    href: "/wp/additional-resources/",
    items: [
      {
        text: "Learning Tools",
        href: "/wp/additional-resources/learning-tools/dev/",
        external: false
      },
      {
        text: "Glossary",
        href: "/wp/understanding-terms/",
        external: false
      },
    ],
  },
];

export default function Navigation() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEls, setAnchorEls] = useState<
    Record<string, HTMLElement | null>
  >({});

  useEffect(() => {
    setMounted(true); //Prevent SSR hydration mismatch
  }, []);

  const toggleDrawer = () => setMobileOpen((prev) => !prev);
  const isActive = (href: string) => pathname === href;

  const handleMenuOpen =
    (label: string) => (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEls((prev) => ({ ...prev, [label]: event.currentTarget }));
    };

  const handleMenuClose = (label: string) => () => {
    setAnchorEls((prev) => ({ ...prev, [label]: null }));
  };

  const handleDropdownItemClick = (label: string) => () => {
    setAnchorEls((prev) => ({ ...prev, [label]: null }));
  };

  if (!mounted) return null;

  return (
    <>
      <AppBar
        position="static"
        elevation={1}
        sx={{
          borderBottom: "none",
          boxShadow: "none",
          padding: { xs: "1rem", md: "1rem" },
          backgroundColor: "white",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            bgcolor: "white",
          }}
        >
          <Link href={"/"} passHref>
            <Image src={SDFLogo} alt="SDF Logo" height={48} priority />
          </Link>
          {isMobile ? (
            <IconButton
              edge="end"
              aria-label="open navigation"
              onClick={toggleDrawer}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              component="nav"
              aria-label="Primary site navigation"
              sx={{
                display: "flex",

                alignItems: "center",
              }}
            >
              {NAV_ITEMS.map(({ label, href, items, external = true }) => (
                <Box key={label}>
                  {items ? (
                    <>
                      <Typography
                        component="a"
                        {...(href
                          ? {
                              href,
                              ...(external
                                ? {
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                  }
                                : {}),
                            }
                          : {})}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          color: "black",
                          fontWeight: "bold",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "1rem",
                          fontFamily: '"Helvetica Neue", sans-serif',
                          textDecoration: "none",
                          "&:hover": {
                            ...(href
                              ? {
                                  textDecoration: "underline !important",
                                  textDecorationThickness: "2px",
                                  textUnderlineOffset: "4px",
                                }
                              : {}),
                          },
                        }}
                      >
                        {label}
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleMenuOpen(label)(e);
                          }}
                          aria-label={`Toggle ${label} menu`}
                        >
                          <ExpandMoreIcon sx={{ fontSize: 18, ml: 0.5 }} />
                        </IconButton>
                      </Typography>
                      <Menu
                        anchorEl={anchorEls[label]}
                        open={Boolean(anchorEls[label])}
                        onClose={handleMenuClose(label)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        {items.map(({ text, href, external = true }) => (
                          <MenuItem
                            key={text}
                            component={external ? "a" : Link}
                            href={href}
                            {...(external
                              ? {
                                  target: "_blank",
                                  rel: "noopener noreferrer",
                                }
                              : {})}
                            onClick={handleDropdownItemClick(label)}
                            sx={{
                              fontFamily: '"Helvetica Neue", sans-serif',
                              fontSize: "0.95rem",
                              fontWeight: isActive(href) ? "bold" : "normal",
                            }}
                          >
                            {text}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : (
                    // <Link href={href ?? "#"} passHref>
                    <Typography
                      component="a"
                      {...(href
                        ? {
                            href,
                            ...(external
                                ? {
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                  }
                                : {}),
                          }
                        : {})}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "black",
                        fontWeight: "bold",
                        cursor: "pointer",
                        textDecoration: "none",
                        fontSize: "1rem",
                        fontFamily: '"Helvetica Neue", sans-serif',
                        "&:hover": {
                          ...(href
                            ? {
                                textDecoration: "underline !important",
                                textDecorationThickness: "2px",
                                textUnderlineOffset: "4px",
                              }
                            : {}),
                        },
                      }}
                    >
                      {label}
                    </Typography>
                    // </Link>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 240,
          },
        }}
      >
        <Box
          onClick={toggleDrawer}
          sx={{ textAlign: "left" }}
          role="presentation"
        >
          <Box sx={{ p: 2 }}>
            <Link href="/" passHref>
              <Image src={SDFLogo} alt="SDF Logo" height={40} priority />
            </Link>
          </Box>
          <List>
            {NAV_ITEMS.map(({ label, href, items, external = true }) => (
              <React.Fragment key={label}>
                <ListItem disablePadding>
                  <Box
                    component="a"
                    href={href ?? "#"}
                    {...(external
                      ? {
                          target: "_blank",
                          rel: "noopener noreferrer",
                        }
                      : {})}
                    sx={{
                      display: "block",
                      width: "100%",
                      px: 2,
                      py: 1,
                      fontWeight: "bold",
                      fontSize: "1rem",
                      textDecoration: "none",
                      color: "inherit",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#F5F5F5",
                      },
                    }}
                  >
                    {label}
                  </Box>
                </ListItem>
                {items &&
                  items.map(({ text, href, external = true }) => (
                    <ListItem key={text} disablePadding>
                      <ListItemButton
                        component={external ? "a" : Link}
                        href={href}
                        {...(external
                          ? {
                              target: "_blank",
                              rel: "noopener noreferrer",
                            }
                          : {})}
                        sx={{
                          pl: 4,
                          fontSize: "0.95rem",
                          bgcolor: isActive(href) ? "#E0F0FF" : "transparent",
                          "&:hover": { bgcolor: "#F5F5F5" },
                          "&:focus": { outline: "none" },
                        }}
                      >
                        <ListItemText
                          primary={text}
                          primaryTypographyProps={{
                            fontWeight: isActive(href) ? "bold" : "normal",
                            fontSize: "0.95rem",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
